import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { PromoBanner } from '@/components/PromoBanner';
import { PACKAGES, ADD_ONS, IMAGES } from '@/lib/data';

const ServicesPage = () => {
  return (
    <div className="pt-28">
      {/* HEADER */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax opacity-25"
          style={{ backgroundImage: `url(${IMAGES.premiumWash})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F1A] via-[#0B0F1A]/70 to-[#0B0F1A]" />
        <div className="luxe-container relative z-10 text-center max-w-3xl mx-auto">
          <div className="luxe-eyebrow mb-4">Services & Pricing</div>
          <h1 className="text-4xl sm:text-6xl text-white leading-[1.05]">
            Luxury Detailing <br />
            <span className="italic text-[#C9A24A]">Services</span> & Pricing.
          </h1>
          <div className="gold-divider mx-auto mt-8" />
          <p className="mt-8 text-white/60 max-w-xl mx-auto">
            Transparent pricing, uncompromised results. Every package is
            available for sedans, SUVs, and select trucks.
          </p>
        </div>
      </section>

      {/* PROMO — inserted between header and grid when active */}
      <PromoBanner variant="feature" />

      {/* PACKAGES GRID */}
      <section className="pb-24">
        <div className="luxe-container grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <div
                data-testid={p.testId}
                className={`luxe-card p-8 h-full flex flex-col ${
                  p.highlight
                    ? 'border-[#C9A24A]/70 shadow-[0_0_40px_-15px_rgba(201,162,74,0.4)]'
                    : ''
                }`}
              >
                {p.highlight && (
                  <span className="luxe-eyebrow text-[10px] mb-3">Most Popular</span>
                )}
                <h3 className="text-2xl text-white mb-2">{p.name}</h3>
                <p className="text-white/50 text-sm mb-6">{p.tagline}</p>

                <div className="flex items-baseline gap-3 mb-6 flex-wrap">
                  <span className="package-price text-4xl">${p.priceSedan}</span>
                  <span className="text-white/40 text-xs uppercase tracking-widest">
                    Sedan
                  </span>
                  <span className="text-white/30">/</span>
                  <span className="package-price text-3xl">${p.priceSuv}</span>
                  <span className="text-white/40 text-xs uppercase tracking-widest">
                    SUV
                  </span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-white/75 text-sm">
                      <Check size={14} className="text-[#C9A24A] mt-1 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/booking?package=${encodeURIComponent(p.name)}`}
                  data-testid={`${p.testId}-book-btn`}
                  className="btn-book w-full !py-3 text-[10px]"
                >
                  Book Now <ArrowRight size={12} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-24 border-t border-white/5 bg-[#0d1220]">
        <div className="luxe-container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="luxe-eyebrow mb-4">Enhancements</div>
              <h2 className="text-3xl sm:text-5xl text-white">
                Premium <span className="italic text-[#C9A24A]">add-ons</span> for the finishing touch.
              </h2>
              <p className="text-white/60 mt-6">
                Combine any add-on with a package for a fully customised
                experience.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADD_ONS.map((a, i) => (
              <Reveal key={a.id} delay={i * 60}>
                <div
                  data-testid={a.testId}
                  className="luxe-card p-6 flex items-center justify-between gap-6"
                >
                  <div>
                    <div className="text-white text-lg">{a.name}</div>
                    <div className="text-white/40 text-xs uppercase tracking-[0.24em] mt-1">
                      Add-On
                    </div>
                  </div>
                  <div className="package-price text-2xl whitespace-nowrap">
                    {a.price}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/booking" data-testid="services-book-cta" className="btn-gold">
              Configure Your Detail <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
